import React from 'react'
import PropTypes from 'prop-types'

import { scaleLinear, scaleOrdinal, scaleTime } from 'd3-scale'
import { range } from 'd3-array'
import { COLORS } from './constants'
import useSize from './useSize'
import {
  stack,
  area,
  stackOffsetSilhouette,
  stackOffsetWiggle,
  curveMonotoneX,
  stackOrderNone,
} from 'd3-shape'

import './style.css'
import data from './data.json'

const f = new Intl.NumberFormat('de-CH').format

const Icon = () => (
  <svg
    width='14'
    height='14'
    ariaHidden='true'
    focusable='false'
    dataPrefix='far'
    dataIcon='hand-pointer'
    role='img'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 448 512'
    class='svg-inline--fa fa-hand-pointer fa-w-14 fa-2x'
  >
    <path
      fill='currentColor'
      d='M358.182 179.361c-19.493-24.768-52.679-31.945-79.872-19.098-15.127-15.687-36.182-22.487-56.595-19.629V67c0-36.944-29.736-67-66.286-67S89.143 30.056 89.143 67v161.129c-19.909-7.41-43.272-5.094-62.083 8.872-29.355 21.795-35.793 63.333-14.55 93.152l109.699 154.001C134.632 501.59 154.741 512 176 512h178.286c30.802 0 57.574-21.5 64.557-51.797l27.429-118.999A67.873 67.873 0 0 0 448 326v-84c0-46.844-46.625-79.273-89.818-62.639zM80.985 279.697l27.126 38.079c8.995 12.626 29.031 6.287 29.031-9.283V67c0-25.12 36.571-25.16 36.571 0v175c0 8.836 7.163 16 16 16h6.857c8.837 0 16-7.164 16-16v-35c0-25.12 36.571-25.16 36.571 0v35c0 8.836 7.163 16 16 16H272c8.837 0 16-7.164 16-16v-21c0-25.12 36.571-25.16 36.571 0v21c0 8.836 7.163 16 16 16h6.857c8.837 0 16-7.164 16-16 0-25.121 36.571-25.16 36.571 0v84c0 1.488-.169 2.977-.502 4.423l-27.43 119.001c-1.978 8.582-9.29 14.576-17.782 14.576H176c-5.769 0-11.263-2.878-14.697-7.697l-109.712-154c-14.406-20.223 14.994-42.818 29.394-22.606zM176.143 400v-96c0-8.837 6.268-16 14-16h6c7.732 0 14 7.163 14 16v96c0 8.837-6.268 16-14 16h-6c-7.733 0-14-7.163-14-16zm75.428 0v-96c0-8.837 6.268-16 14-16h6c7.732 0 14 7.163 14 16v96c0 8.837-6.268 16-14 16h-6c-7.732 0-14-7.163-14-16zM327 400v-96c0-8.837 6.268-16 14-16h6c7.732 0 14 7.163 14 16v96c0 8.837-6.268 16-14 16h-6c-7.732 0-14-7.163-14-16z'
      class=''
    ></path>
  </svg>
)

const annotations = [
  {
    year: 1872,
    event: 'Start Bau Gotthardtunnel',
    mobile: true,
  },
  {
    year: 1916,
    event: 'Erster Weltkrieg',
    mobile: false,
  },
  {
    year: 1942,
    event: 'Zweiter Weltkrieg',
    mobile: true,
  },
  {
    year: 1970,
    event: 'Schwarzenbach-Initiative',
    mobile: false,
  },
  {
    year: 1973,
    event: 'Ölkrise',
    bottom: true,
    mobile: true,
  },
    // {
    //   year: 2002,
    //   event: 'Personenfreizügigkeit',
    //   mobile: true,
    // },
]

const Chart = ({ width, height, x, y, clip, title, description, source }) => {
  const [headerRef, headerSize] = useSize()
  const [footerRef, footerSize] = useSize()
  const chartHeight = height - headerSize.height - footerSize.height

  const isMobile = width < 600

  const margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  }

  const gutter = {
    top: 0,
    right: isMobile ? 0 : 100,
    bottom: 15,
    left: isMobile ? 0 : 50,
  }

  const w = width - margin.left - margin.right
  const h = chartHeight - margin.top - margin.bottom

  const maxY = 2224441
  const yDomain = [-maxY / 2, maxY / 2]
  const yTicks = range(...yDomain, 500000)
  const yScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([h - gutter.bottom - gutter.top, 1]),
    [h, y]
  )

  const xDomain = [1850, 2018]
  const xTicks = range(1850, 2018, 35).concat(2018)
  const xScale = React.useMemo(
    () =>
      scaleTime()
        .domain(xDomain)
        .range([gutter.left, w - gutter.right - 1]),
    [w, x]
  )

  const keys = [
    'Andere',
    'Asien',
    'Andere Europa',
    'Türkei',
    'Spanien',
    'Frankreich',
    'Portugal',
    '(Ex-)Jugoslawien',
    'Deutschland',
    'Italien',
  ]
  const stacked = stack()
    .keys(keys)
    .order(stackOrderNone)
    .offset(stackOffsetSilhouette)(data)
  const pathFn = area()
    .curve(curveMonotoneX)
    .x((d) => xScale(d.data.year))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]))
  var color = scaleOrdinal().domain(keys).range(COLORS.categories)
  var colorLight = scaleOrdinal().domain(keys).range(COLORS.categoriesLight)

  const [active, setActive] = React.useState(null)
  const latestNumbers = data[data.length - 1]
  const sum = React.useMemo(
    () => Object.values(latestNumbers).reduce((acc, cur) => acc + cur, 0),
    [latestNumbers]
  )

  return (
    <div className='chart' style={{ height }}>
      <div ref={headerRef}>
        {title && <div className='title'>{title}</div>}
        {description && <div className='description'>{description}</div>}
      </div>

      <div
        style={{ position: 'relative', width, chartHeight }}
        className='description'
      >
        {!isMobile && (
          <div
            style={{
              position: 'absolute',
              top: 0.7 * chartHeight,
              left: xScale(1872),
              width: 250,
              zIndex: 100,
            }}
          >
            {active ? (
              <div>
                <span style={{ fontWeight: 800 }}>{active}</span>
                <br />
                2018 wohnten{' '}
                <span style={{ fontWeight: 800 }}>
                  {f(latestNumbers[active])} Personen
                </span>{' '}
                aus dieser Region in der Schweiz, dies entsprach{' '}
                <span style={{ fontWeight: 800 }}>
                  {f(((latestNumbers[active] / sum) * 100).toFixed(0))} Prozent
                </span>{' '}
                der ausländischen Bevölkerung.
              </div>
            ) : (
              <div>
                <Icon /><br/>
                Fahren Sie mit der Maus über die Flächen, um mehr über die
                Herkunftsländer zu erfahren.
              </div>
            )}
          </div>
        )}
        {isMobile && (
          <div
            style={{
              position: 'absolute',
              top: 0.61 * chartHeight,
              left: xScale(1882.5),
              zIndex: 100,
            }}
            className='legend'
          >
            <table style={{borderSpacing: 0}}>
              {[...keys].reverse().map((key) => (
                <tr>
                  <td style={{padding: '0 4px 0 0'}}>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 10,
                        backgroundColor: color(key),
                        display: 'inline-block',
                        marginRight: 5,
                        border: '1px solid #C8C8D1'
                      }}
                    />
                    {key}{' '}
                  </td>
                  <td style={{padding: 0, textAlign: 'right'}}>
                    <span style={{ color: '#9698A8' }}>
                      {/* {f(latestNumbers[key].toFixed(1))} */}
                    </span>
                  </td>
                </tr>
              ))}
            </table>
          </div>
        )}
        {annotations
          .filter((a) => (isMobile ? a.mobile : true))
          .map((a) => (
            <div
              style={{
                position: 'absolute',
                left: xScale(a.year),
                top: a.bottom ? (chartHeight - 60) / 2 : 20,
                paddingLeft: 5,
                width: 60,
                display: 'flex',
                alignItems: a.bottom ? 'flex-end' : 'flex-start',
                lineHeight: 1.2,
                height: (chartHeight - gutter.bottom) / 2,
                borderLeft: '1px dotted #8F8F8F',
                zIndex: 0,
              }}
              className='annotation'
            >
              <span><strong>{a.event}</strong></span>
            </div>
          ))}

        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 0,
          }}
        >
          <svg width={width} height={chartHeight}>
            <defs>
              <clipPath id='margin'>
                <rect
                  x={margin.left - 1}
                  y={margin.top - 1}
                  width={w + 1}
                  height={h + 1}
                />
              </clipPath>
            </defs>
            <g clipPath={clip ? 'url(#margin)' : undefined}>
              <g transform={`translate(${margin.left},${margin.top})`}>
                {yTicks
                  .filter((t, i) => i > 0)
                  .map((t) => (
                    <line
                      x1={gutter.left}
                      x2={gutter.left + 10}
                      y1={yScale(t)}
                      y2={yScale(t)}
                      stroke={COLORS.grid}
                      strokeWidth={1}
                    />
                  ))}
                {xTicks.map((t) => (
                  <g transform={`translate(${xScale(t)}, 0)`}>
                    <line
                      y1={0.95 * h}
                      y2={h - gutter.bottom}
                      stroke={COLORS.grid}
                      strokeWidth={1}
                    />
                  </g>
                ))}
              </g>
            </g>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {yTicks
                .filter((d, i, arr) => i > 0)
                .map((t, i) => (
                  <text
                    x={xScale(xDomain[0]) + (isMobile ? 13 : -3)}
                    y={yScale(t)}
                    alignmentBaseline='central'
                    fill={COLORS.grid}
                    textAnchor={isMobile ? 'start' : 'end'}
                  >
                    {`${(t - yDomain[0]) / 1e6} Mio.`}
                  </text>
                ))}
              {xTicks.map((t, i) => (
                <text
                  x={xScale(t)}
                  y={h - gutter.bottom + 3}
                  alignmentBaseline='hanging'
                  textAnchor={
                    isMobile
                      ? i > 0
                        ? i > xTicks.length - 2
                          ? 'end'
                          : 'middle'
                        : 'start'
                      : 'middle'
                  }
                >
                  {t}
                </text>
              ))}
              <g onMouseLeave={() => setActive(null)}>
                {stacked.map((d) => {
                  const path = pathFn(d)
                  return (
                    <path
                      d={path}
                      fill={
                        active
                          ? active == d.key
                            ? color(d.key)
                            : colorLight(d.key)
                          : color(d.key)
                      }
                      onTouchStart={() => !isMobile && setActive(d.key)}
                      onMouseEnter={() => !isMobile && setActive(d.key)}
                      stroke='#fff'
                      // stroke='#202346'
                      style={{
                        cursor: isMobile ? 'inherit' : 'pointer',
                      }}
                    />
                  )
                })}
              </g>
              {keys.map((k, i) => (
                <g>
                  <text
                    x={w + 2 - gutter.right}
                    y={
                      4 +
                      yScale(stacked[i][stacked[i].length - 1][0]) -
                      (yScale(stacked[i][stacked[i].length - 1][0]) -
                        yScale(stacked[i][stacked[i].length - 1][1])) /
                        2
                    }
                    textAnchor={'start'}
                  >
                    {k}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </div>
      <div ref={footerRef} className='footer'>
        <div>{source}</div>
      </div>
    </div>
  )
}

Chart.defaultProps = {
  width: 200,
  height: 200,
  x: [],
  y: [],
  clip: false,
}

export default Chart
